import * as React from 'react'
import {CSSProperties, useCallback, useEffect, useMemo, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {
  DataObjectId,
  Hub,
  Tag,
  taskTemplatesContainerId,
  TimetrackerBoardSettings
} from 'yougile-model'
import {UiSlot} from '@api/components'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {Icon} from '@components/common/base/icon'
import {PinStickersDropdown} from '@components/sticker/pin-sticker-menu'
import {noPropagate} from '@containers/hooks'
import {Column} from '@extensions/column'
import {Sticker} from '@extensions/sticker/sticker'
import {StickerFilter} from '@extensions/sticker/sticker-filter'
import {SubtaskUtil} from '@extensions/subtask-util'
import {getBoardByTask, Task} from '@extensions/task'
import {StickerItem} from './sticker-item'

interface TaskStickersProps {
  pinnedTags: Tag[]
  task?: Hub
  firstChildren?: React.ReactNode | React.ReactNode[]
  children?: React.ReactNode[]
  containerKey?: string
  filtersController?: StickerFilter
  disableEdit?: boolean
  isParentTaskTemplate?: boolean
  asSubtask?: boolean
  cbEmptyStickers?: () => void
  showButtonAlways?: boolean
  isInBoard?: boolean
  isCompleted?: boolean
  timeTrackingConf?: TimetrackerBoardSettings
  columnId?: DataObjectId
  requireTimeTrackingBeforeClose?: {
    stickerId: DataObjectId
    fromColumnId: DataObjectId
  }
  onCloseRequiredTimeTrackingSticker?: (sticker: Tag) => void
  taskInColumn?: boolean
}

const TwTasksStickers = tw.div`
  flex
  flex-wrap
  gap-4
`

const TwTasksStickersWrapper = tw.div<{$isUserSticker: boolean}>`
  ${({$isUserSticker}) =>
    $isUserSticker ? `absolute bottom-0 -right-[44px] sticker-item-user` : ''}
`

const TwAddStickerButton = tw.div<{
  $menuIsVisible?: boolean
  $short: boolean
  $isTaskColored?: boolean
  $isCompleted?: boolean
}>`
  group/add-sticker
  flex
  items-center
  text-12
  gap-4
  h-[24px]
  invisible
  group-hover:visible
  cursor-pointer
  ${({$short}) =>
    $short
      ? 'px-0 w-24 justify-center'
      : 'outline-1 outline-dashed hover:outline-action-hover pl-4 pr-6 rounded-[8px] -outline-offset-1'}

  hover:text-action-hover

  ${({$menuIsVisible, $isTaskColored, $isCompleted}) => {
    if ($menuIsVisible) {
      return `!visible ${
        $isTaskColored
          ? '!text-icon-primary !outline-icon-primary'
          : '!text-action-pressed !outline-action-pressed'
      }`
    } else {
      if ($isTaskColored) {
        return `outline-icon-primary text-icon-primary hover:text-icon-primary hover:outline-icon-primary ${
          $isCompleted
            ? 'opacity-[.38] hover:opacity-[.64]'
            : 'opacity-[.64] hover:opacity-100'
        }`
      }

      return $isCompleted
        ? 'text-placeholder-new outline-placeholder-new'
        : 'text-grey outline-grey'
    }
  }}
`

export const TaskStickers = (props: TaskStickersProps) => {
  const {task, showButtonAlways, isInBoard, isCompleted} = props
  const [openMenu, setOpenMenu] = useState<DataObjectId[]>([])
  const {children, firstChildren, isParentTaskTemplate, asSubtask} = props
  const hasChildren = checkChildren(firstChildren) || checkChildren(children)
  const isTaskInSimpleCol = useMemo(() => {
    if (props.columnId) {
      const column = Column.getById(props.columnId)
      return column && !column?.data?.report && !Column.isMirror(column)
    }
    return false
  }, [props.columnId])

  useEffect(() => {
    if (
      props.taskInColumn &&
      isTaskInSimpleCol &&
      props.requireTimeTrackingBeforeClose?.stickerId
    ) {
      const sticker = props.requireTimeTrackingBeforeClose?.stickerId
      setOpenMenu(prev => [...prev, sticker])
    }
  }, [
    isTaskInSimpleCol,
    props.requireTimeTrackingBeforeClose,
    props.taskInColumn
  ])

  const isTaskTemplate = Task.isTaskTemplate(task)
  const isAnyTaskTemplate = isTaskTemplate || isParentTaskTemplate

  const stickersMenuList =
    props.containerKey !== 'stats'
      ? task?.id && getStickersList(task.id)
      : undefined

  const canEdit = useMemo(
    () =>
      task &&
      (PermissionsChecker.checkEditTaskStickers(task, 'not-user', undefined) ||
        isAnyTaskTemplate),
    [task, isAnyTaskTemplate]
  )

  const pinStickerToTask = useCallback(
    (sticker: Tag) => {
      if (task) {
        Sticker.pin(sticker, task.id, task)
        setOpenMenu(prev => [...prev, sticker.id])
      }
    },
    [task]
  )

  const onCloseStickerMenu = useCallback((sticker: Tag) => {
    if (
      sticker.id === props.requireTimeTrackingBeforeClose?.stickerId &&
      props.onCloseRequiredTimeTrackingSticker
    ) {
      props.onCloseRequiredTimeTrackingSticker(sticker)
    }
    setOpenMenu(prev => prev.filter(id => id !== sticker.id))
  }, [])

  useEffect(() => {
    if (!stickersMenuList?.length) {
      props.cbEmptyStickers?.()
    }
  }, [stickersMenuList?.length])

  const short = isShort(
    !!isTaskTemplate,
    props.pinnedTags,
    !!hasChildren,
    asSubtask
  )

  return (
    <TwTasksStickers>
      {firstChildren}
      <UiSlot
        name="task-stickers-before"
        data={{
          taskId: task?.id
        }}
      />
      {props.pinnedTags.map(sticker => {
        const isUserSticker = Sticker.getType(sticker) === 'user'

        return (
          <TwTasksStickersWrapper
            $isUserSticker={isUserSticker}
            key={sticker.id}
            onMouseDown={noPropagate}
            onClick={noPropagate}
          >
            <StickerItem
              draggable
              withTooltip
              sticker={sticker}
              location="task"
              isInBoard={isInBoard}
              taskId={task?.id}
              menuIsOpened={openMenu.includes(sticker.id)}
              onCloseMenu={onCloseStickerMenu}
              filtersController={props.filtersController}
              withMenu={!props.disableEdit}
              isTaskTemplate={isAnyTaskTemplate}
            />
          </TwTasksStickersWrapper>
        )
      })}
      <UiSlot
        name="task-stickers-after"
        data={{
          taskId: task?.id
        }}
      />
      {children}
      {!!stickersMenuList?.length && canEdit && (
        <div onMouseDown={noPropagate} onClick={noPropagate}>
          <PinStickersDropdown
            stickers={stickersMenuList}
            onClickOnSticker={pinStickerToTask}
          >
            <IconPlusWithTooltip
              short={short}
              showButtonAlways={showButtonAlways}
              isTaskColored={props.task && Task.isTaskColored(props.task)}
              isCompleted={isCompleted}
            />
          </PinStickersDropdown>
        </div>
      )}
    </TwTasksStickers>
  )
}

const isShort = (
  isTaskTemplate: boolean,
  pinnedTags: Tag[],
  hasChildren: boolean,
  asSubtask?: boolean
  // eslint-disable-next-line max-params
): boolean => {
  if (isTaskTemplate && !asSubtask) {
    return !!pinnedTags?.length
  }

  return !!hasChildren || !pinnedTags?.length || pinnedTags.length !== 1
}

const TwIconPlus = tw(Icon)<{
  $menuIsVisible?: boolean
  $isTaskColored?: boolean
  $isCompleted?: boolean
}>`
  ${p => {
    if (p.$menuIsVisible) {
      return p.$isTaskColored ? '!text-icon-primary' : '!text-action-pressed'
    }

    return p.$isTaskColored
      ? 'text-icon-primary group-hover/add-sticker:text-icon-primary'
      : `${
          p.$isCompleted ? 'text-placeholder-new' : 'text-icon-secondary'
        } group-hover/add-sticker:text-action-hover`
  }}
`

const IconPlusWithTooltip = ({
  $menuIsVisible,
  short,
  showButtonAlways,
  isTaskColored,
  isCompleted
}: {
  short: boolean
  $menuIsVisible?: boolean
  showButtonAlways?: boolean
  isTaskColored?: boolean
  isCompleted?: boolean
}) => {
  const style: CSSProperties = showButtonAlways ? {visibility: 'visible'} : {}

  return (
    <TwAddStickerButton
      data-test="task-add-sticker-button"
      style={style}
      $menuIsVisible={$menuIsVisible}
      $short={short}
      $isTaskColored={isTaskColored}
      $isCompleted={isCompleted}
    >
      <TwIconPlus
        size={short ? 24 : 16}
        icon={short ? 'IconCustomPlusDashed' : 'plus'}
        style={style}
        $isTaskColored={isTaskColored}
        $isCompleted={isCompleted}
        $menuIsVisible={$menuIsVisible}
      />
      {short ? '' : Lang.msg.sticker}
    </TwAddStickerButton>
  )
}

const getStickersList = (taskId: DataObjectId): Tag[] => {
  const task = Task.getById(taskId)

  if (!task) {
    return []
  }

  const isTaskTemplate = taskTemplatesContainerId === task.location

  if (isTaskTemplate) {
    const parentId = SubtaskUtil.getParentTaskIds(task.id)[0]

    return parentId
      ? Sticker.list(getBoardByTask(parentId))
      : Sticker.list(getBoardByTask(task.id))
  }

  const box = Sync.getCurrentSandbox()
  const parents = Task.getAllColumnLevelParentTaskIds(box, task.id)
  const stickersList = new Set<Tag>()

  for (const parentId of parents) {
    const parentTaskLocation = Task.getLocations(parentId)?.board

    Sticker.list(parentTaskLocation).forEach(sticker => {
      stickersList.add(sticker)
    })
  }

  return Array.from(stickersList)
}

const checkChildren = (
  children?: React.ReactNode | React.ReactNode[]
): boolean => {
  if (!children) {
    return false
  }
  if (Array.isArray(children)) {
    return children?.some(child => !!child)
  }
  return true
}
